import React from 'react';
import { get } from 'lodash';

// React view component to render
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { memberDetailsHeaderWidget as MemberDetailsHeaderWidgetView } from 'ui/views/widgets/memberDetailsHeader';
import { MemberDetailsHeaderWidgetService } from 'services/memberDetailsHeader';
import { withRouter } from 'react-router-dom';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberDetailsHeaderWidget } from 'ui/containers/MemberDetailsHeaderWidget';
 *
 * // render
 * <MemberDetailsHeaderWidget propName='propValue'/>
 */
class _MemberDetailsHeaderWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind methods
        this.handleMemberSwitch = this.handleMemberSwitch.bind( this );
        this.fetchLiveMemberStatus = this.fetchLiveMemberStatus.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            isFetchingActions: false,
            isCheckingLiveMemberStatus: false,
            liveMemberStatusAsOf: null,
            liveMemberStatusError: null,
        };

        this.active = true;
        this.selectionEpoch = 0;
        this.fetchEpoch = 0;
        this.statusEpoch = 0;
        this.pendingStatusSelection = null;

        // create service instance
        this.memberDetailsHeaderWidgetService = new MemberDetailsHeaderWidgetService();

        // start component data fetch
        this.fetchComponentData();
    }

    /******************************************************/

    /**
     * @desc Fetch member detail information.
     */
    async fetchComponentData() {
        const selection = this.selectionEpoch;
        const request = ++this.fetchEpoch;
        this.memberDetailsHeaderWidgetService.cancel();
        try {
            const { status, data } = await this.memberDetailsHeaderWidgetService.fetchData( {
                url: this.props.widget.url,
                data: this.props.requestPayload,
            } );
            if( ! this.isCurrentFetch( selection, request ) ) return;
            this.setState( () => this.isCurrentFetch( selection, request ) ? {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS === status ? SERVICE_RESPONSE_STATUS.SUCCESS : SERVICE_RESPONSE_STATUS.FAILURE,
                ...( SERVICE_RESPONSE_STATUS.SUCCESS === status ? { componentData: data } : {} ),
            } : null );
        } catch( e ) {
            if( ! this.isCurrentFetch( selection, request ) ) return;
            this.setState( () => this.isCurrentFetch( selection, request ) ? {
                isFetching: false, networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
            } : null );
        }
    }

    isCurrentFetch( selection, request ) {
        return this.active && selection === this.selectionEpoch && request === this.fetchEpoch;
    }

    isCurrentStatus( selection, request ) {
        return this.active && selection === this.selectionEpoch && request === this.statusEpoch;
    }

    /** Fetch actions for the selected family member. */
    handleMemberSwitch( selection ) {
        const context = get( this.props, 'requestPayload.context', {} );
        this.pendingStatusSelection = selection.memberId === context.memberId && selection.patientId === context.patientId
            ? null : { memberId: selection.memberId, patientId: selection.patientId };
        const epoch = ++this.selectionEpoch;
        const request = ++this.fetchEpoch;
        ++this.statusEpoch;
        this.memberDetailsHeaderWidgetService.cancel();
        this.setState( () => this.isCurrentFetch( epoch, request ) ? {
            isFetchingActions: true,
            isCheckingLiveMemberStatus: false,
            liveMemberStatusAsOf: null,
            liveMemberStatusError: null,
        } : null, async () => {
            if( ! this.isCurrentFetch( epoch, request ) ) return;
            try {
                const { status, data } = await this.memberDetailsHeaderWidgetService.fetchData( {
                    url: this.props.widget.url,
                    data: {
                        ...this.props.requestPayload,
                        context: { ...this.props.requestPayload.context, ...selection },
                    },
                } );
                if( ! this.isCurrentFetch( epoch, request ) ) return;
                this.setState( prevState => this.isCurrentFetch( epoch, request ) ? {
                    isFetching: false,
                    isFetchingActions: false,
                    ...( SERVICE_RESPONSE_STATUS.SUCCESS === status ? {
                        networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                        componentData: { ...prevState.componentData, ...data },
                    } : {} ),
                } : null );
            } catch( e ) {
                if( ! this.isCurrentFetch( epoch, request ) ) return;
                this.setState( () => this.isCurrentFetch( epoch, request ) ? {
                    isFetching: false, isFetchingActions: false, networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
                } : null );
            }
        } );
    }

    /** Fetch live member status from ePost and update displayed status. */
    async fetchLiveMemberStatus() {
        if( this.pendingStatusSelection ) return;
        const selection = this.selectionEpoch;
        const request = ++this.statusEpoch;
        if( !get( this.props.requestPayload, 'context.patientId' ) ) {
            this.setState( () => this.isCurrentStatus( selection, request ) ? { liveMemberStatusError: 'FETCH_FAILED' } : null );
            return;
        }
        this.setState( () => this.isCurrentStatus( selection, request ) ? { isCheckingLiveMemberStatus: true, liveMemberStatusError: null } : null );
        try {
            const { status, data, httpStatus } = await this.memberDetailsHeaderWidgetService.fetchLiveMemberStatus( {
                data: this.props.requestPayload,
            } );
            if( ! this.isCurrentStatus( selection, request ) ) return;
            this.setState( prevState => {
                if( ! this.isCurrentStatus( selection, request ) ) return null;
                if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) return {
                    isCheckingLiveMemberStatus: false,
                    liveMemberStatusAsOf: get( data, 'asOf', null ),
                    liveMemberStatusError: null,
                    componentData: { ...prevState.componentData, ...get( data, 'memberData', {} ) },
                };
                return { isCheckingLiveMemberStatus: false, liveMemberStatusError: httpStatus === 404 ? 'STATUS_UNAVAILABLE' : 'FETCH_FAILED' };
            } );
        } catch( e ) {
            if( ! this.isCurrentStatus( selection, request ) ) return;
            this.setState( () => this.isCurrentStatus( selection, request ) ? {
                isCheckingLiveMemberStatus: false, liveMemberStatusError: 'FETCH_FAILED',
            } : null );
        }
    }

    componentDidUpdate( previousProps ) {
        const previous = get( previousProps, 'requestPayload.context', {} );
        const current = get( this.props, 'requestPayload.context', {} );
        if( previousProps.memberId !== this.props.memberId || previous.memberId !== current.memberId ||
            previous.patientId !== current.patientId || get( previousProps, 'widget.url' ) !== get( this.props, 'widget.url' ) ) {
            ++this.selectionEpoch;
            ++this.statusEpoch;
            if( previous.memberId !== current.memberId || previous.patientId !== current.patientId ) {
                this.pendingStatusSelection = null;
            }
            const epoch = this.selectionEpoch;
            this.setState( () => this.active && epoch === this.selectionEpoch ? {
                isFetching: true, componentData: undefined, isFetchingActions: false,
                isCheckingLiveMemberStatus: false, liveMemberStatusAsOf: null, liveMemberStatusError: null,
            } : null, () => {
                if( this.active && epoch === this.selectionEpoch ) this.fetchComponentData();
            } );
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.active = false;
        ++this.selectionEpoch;
        ++this.fetchEpoch;
        ++this.statusEpoch;
        this.memberDetailsHeaderWidgetService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberDetailsHeaderWidgetView
                memberId={ this.props.memberId }
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                { ...get( this.state, 'componentData', {} ) }
                onGenerateActionListLog={ this.props.onGenerateActionListLog }
                requestPayload={ this.props.requestPayload }
                onMemberSwitch={ this.handleMemberSwitch }
                isFetchingActions={ this.state.isFetchingActions }
                isCheckingLiveMemberStatus={ this.state.isCheckingLiveMemberStatus }
                liveMemberStatusAsOf={ this.state.liveMemberStatusAsOf }
                liveMemberStatusError={ this.state.liveMemberStatusError }
                onCheckLiveMemberStatus={ this.fetchLiveMemberStatus }
            />
        );
    }
}

// set display name
_MemberDetailsHeaderWidget.displayName = '_MemberDetailsHeaderWidget';

// set default props
_MemberDetailsHeaderWidget.defaultProps = {
    memberId: null,
    requestPayload: {},
};

// prop types
_MemberDetailsHeaderWidget.propTypes = {};

/*********************************************/

// wrap container with router
export const MemberDetailsHeaderWidget = withRouter( _MemberDetailsHeaderWidget );
